import { type Ref, useCallback } from 'react'
import { useIntl } from 'react-intl'
import {
  faCircleCheck,
  faCircleInfo,
  faSpinner,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { clsx } from 'clsx'

import { ColorPicker } from './ColorPicker'
import { TagAutocomplete } from './TagAutocomplete'
import type { TagAutocompleteHandle } from './TagAutocomplete.types'
import { TAG_COLORS } from './tagging.constants'
import type { Tag } from './tagging.types'
import { useTagging } from './useTagging'
import { useOptionalTaggingTableSelection } from './useTaggingTableSelection'

export type EditorDropdownPlacement = 'start' | 'end'

interface EditorDropdownProps {
  placement?: EditorDropdownPlacement
}

export const EditorDropdown = (props: EditorDropdownProps) => {
  const { placement = 'end' } = props
  const intl = useIntl()
  const tagging = useTagging()
  const selection = useOptionalTaggingTableSelection()
  const selectedCount = selection?.selectedCount ?? 1

  const { setInputText, setSelectedColor } = tagging

  const handleAutocompleteSelect = useCallback(
    (tag: Tag) => {
      setInputText(tag.name)
      setSelectedColor(tag.color)
    },
    [setInputText, setSelectedColor],
  )

  if (!tagging.canSubmit && tagging.orgTags.length === 0) {
    return null
  }

  return (
    <div
      className={clsx('EditorDropdown', {
        'EditorDropdown--end': placement === 'end',
        'EditorDropdown--loading': tagging.isAddingTag,
        'EditorDropdown--start': placement === 'start',
      })}
    >
      <div className="EditorDropdown-controls">
        <ColorPicker
          colors={TAG_COLORS}
          onChange={tagging.setSelectedColor}
          selectedColor={tagging.selectedColor}
        />

        <button
          aria-label={intl.formatMessage({ id: 'Tags_confirm_tag' })}
          className={clsx('EditorDropdown-submit', {
            'EditorDropdown-submit--active':
              tagging.canSubmit && !tagging.isAddingTag,
            'EditorDropdown-submit--loading': tagging.isAddingTag,
          })}
          disabled={!tagging.canSubmit || tagging.isAddingTag}
          onClick={tagging.handleSubmit}
          type="button"
        >
          <FontAwesomeIcon
            icon={tagging.isAddingTag ? faSpinner : faCircleCheck}
            spin={tagging.isAddingTag}
          />
        </button>
      </div>

      {selectedCount > 1 && (
        <div className="EditorDropdown-info">
          <FontAwesomeIcon
            className="EditorDropdown-infoIcon"
            icon={faCircleInfo}
          />
          <span>
            {intl.formatMessage(
              { id: 'Tags_applying_to_count' },
              { count: selectedCount },
            )}
          </span>
        </div>
      )}

      <TagAutocomplete
        ref={tagging.autocompleteRef as Ref<TagAutocompleteHandle>}
        onFocusInput={() => tagging.inputRef.current?.focus()}
        onSelect={handleAutocompleteSelect}
        orgTags={tagging.orgTags}
        query={tagging.inputText}
      />
    </div>
  )
}
